import { useState, useRef } from 'react'
import clsx from 'clsx'
import styles from './Carousel.module.scss'
import { CarouselProps } from './types'

export const Carousel = ({
  items,
  className,
  arrows: {
    active: arrowsActive = true,
    layout: arrowsLayout = 'inside',
    yPosition: arrowsYPosition = 'center',
    xPosition: arrowsXPosition = 'auto',
  } = {},
  dots: {
    active: dotsActive = true,
    layout: dotsLayout = 'inside',
    yPosition: dotsYPosition = 'bottom',
    xPosition: dotsXPosition = 'center',
    max: dotsMax = 5,
  } = {},
  infinite = false,
}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [dragDistance, setDragDistance] = useState(0)
  const itemsRef = useRef<HTMLDivElement>(null)

  const touchStartX = useRef(0)
  const isDragging = useRef(false)

  const handleNext = () => {
    let nextIndex = currentIndex

    do {
      nextIndex = nextIndex < items.length - 1 ? nextIndex + 1 : infinite ? 0 : currentIndex
    } while (items[nextIndex]?.disabled && nextIndex !== currentIndex) // Skip disabled items

    if (nextIndex !== currentIndex) {
      setCurrentIndex(nextIndex)
    }
  }

  const handlePrev = () => {
    let prevIndex = currentIndex

    do {
      prevIndex = prevIndex > 0 ? prevIndex - 1 : infinite ? items.length - 1 : currentIndex
    } while (items[prevIndex]?.disabled && prevIndex !== currentIndex) // Skip disabled items

    if (prevIndex !== currentIndex) {
      setCurrentIndex(prevIndex)
    }
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return
    const touchMoveX = e.touches[0].clientX
    const dragAmount = touchMoveX - touchStartX.current

    // Disable drag left if on the first item in non-infinite mode
    if (!infinite && currentIndex === 0 && dragAmount > 0) {
      return
    }

    // Disable drag right if on the last item in non-infinite mode
    if (!infinite && currentIndex === items.length - 1 && dragAmount < 0) {
      return
    }

    setDragDistance(dragAmount) // Update drag distance based on touch movement
  }

  const handleTouchEnd = () => {
    const carouselWidth = itemsRef.current?.offsetWidth || 0
    const swipeThreshold = 0.15 * carouselWidth // 15% of the carousel width

    isDragging.current = false

    if (Math.abs(dragDistance) > swipeThreshold) {
      // Change slide if drag is greater than 15% of the carousel width
      if (dragDistance < 0) {
        handleNext() // Swipe left
      } else {
        handlePrev() // Swipe right
      }
    }

    setDragDistance(0) // Reset drag distance after swipe ends
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    isDragging.current = true
  }

  const renderDots = () => {
    if (!dotsActive || dotsMax < 2 || items.length < 2) {
      return null
    }

    const totalDots = items.length
    const halfMax = Math.floor(dotsMax / 2)
    let start = Math.max(0, currentIndex - halfMax)
    const end = Math.min(totalDots, start + dotsMax)

    if (end === totalDots) {
      start = Math.max(0, totalDots - dotsMax)
    }

    const visibleDots = items.slice(start, end)

    return (
      <div
        className={clsx(
          styles.dotsHolder,
          styles[dotsLayout],
          styles[dotsXPosition],
          styles[dotsYPosition],
        )}
      >
        {arrowsActive && items.length > 1 && arrowsLayout === 'withDots' && (
          <div
            className={clsx(
              styles.arrows,
              styles.withDots,
              styles.withDotsLeft,
              styles[dotsLayout],
              styles[dotsYPosition],
            )}
          >
            {(arrowsXPosition === 'left' ||
              arrowsXPosition === 'auto' ||
              arrowsXPosition === undefined) &&
              prevArrow}
            {arrowsXPosition === 'left' && nextArrow}
          </div>
        )}

        <div
          className={clsx(
            styles.dots,
            styles[dotsLayout],
            styles[dotsYPosition],
            styles[dotsXPosition],
          )}
        >
          {visibleDots.map((_, index) => {
            const dotIndex = start + index
            const item = items[dotIndex]
            return (
              <button
                key={dotIndex}
                className={clsx(styles.dot, {
                  [styles.active]: dotIndex === currentIndex,
                  [styles.disabled]: item?.disabled, // Add disabled class for disabled items
                })}
                onClick={() => !item?.disabled && setCurrentIndex(dotIndex)}
                disabled={item?.disabled} // Disable click if item is disabled
              />
            )
          })}
        </div>

        {arrowsActive && arrowsLayout === 'withDots' && (
          <div
            className={clsx(
              styles.arrows,
              styles.withDots,
              styles.withDotsRight,
              styles[dotsLayout],
              styles[dotsYPosition],
            )}
          >
            {arrowsXPosition === 'right' && prevArrow}
            {(arrowsXPosition === 'right' ||
              arrowsXPosition === 'auto' ||
              arrowsXPosition === undefined) &&
              nextArrow}
          </div>
        )}
      </div>
    )
  }

  const prevArrow = (
    <button
      className={clsx(styles.prevArrow, { [styles.disabled]: !infinite && currentIndex === 0 })}
      onClick={handlePrev}
      disabled={!infinite && currentIndex === 0}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
        <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
      </svg>
    </button>
  )

  const nextArrow = (
    <button
      className={clsx(styles.nextArrow, {
        [styles.disabled]: !infinite && currentIndex === items.length - 1,
      })}
      onClick={handleNext}
      disabled={!infinite && currentIndex === items.length - 1}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
        <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
      </svg>
    </button>
  )

  return (
    <div
      className={clsx(
        styles.carousel,
        { [styles.flexRow]: arrowsLayout === 'outside' && arrowsYPosition === 'center' },
        className,
      )}
    >
      {arrowsActive &&
        items.length > 1 &&
        arrowsLayout === 'outside' &&
        arrowsYPosition === 'center' && (
          <div className={clsx(styles.arrows, styles.outside, styles.outsideArrowPrev)}>
            {prevArrow}
          </div>
        )}

      <div
        className={styles.carouselInner}
        ref={itemsRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {items.map((child, index) => (
          <div
            key={index}
            className={clsx(styles.carouselItem, {
              [styles.active]: index === currentIndex,
            })}
            style={{
              transform: `translateX(calc(-${currentIndex * 100}% + ${dragDistance}px))`,
              transition: dragDistance ? 'none' : ' all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {child.content}
          </div>
        ))}
      </div>

      {arrowsActive &&
        items.length > 1 &&
        arrowsLayout === 'outside' &&
        arrowsYPosition === 'center' && (
          <div className={clsx(styles.arrows, styles.outside, styles.outsideArrowNext)}>
            {nextArrow}
          </div>
        )}

      {arrowsActive && items.length > 1 && (
        <>
          {arrowsLayout !== 'withDots' &&
            !(arrowsLayout === 'outside' && arrowsYPosition === 'center') && (
              <div
                className={clsx(
                  styles.arrows,
                  styles[arrowsLayout],
                  styles[arrowsXPosition],
                  styles[arrowsYPosition],
                )}
              >
                {prevArrow}
                {nextArrow}
              </div>
            )}
        </>
      )}

      {renderDots()}
    </div>
  )
}
